import React, { ComponentProps } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import {
  CompareRoot,
  SectionTitle,
  Table,
  Row,
  TableContainer,
  BrandColumn,
  ThemColumn,
  UsColumn,
  CompareImageWrapper,
  Blob5Wrapper,
  Blob6Wrapper,
  BlobRectangleWrapper,
  MobileWrapper,
  FoodImage,
  FoodWrapper,
  CarouselItem,
} from './Compare.styles'
import Trans from 'next-translate/Trans'
import Blob5 from '../../../../../public/assets/recipes/categories/blob5.webp'
import Blob6 from '../../../../../public/assets/recipes/categories/blob6.webp'
import Comparison from '../../../../../public/assets/recipes/categories/comparison.webp'
import BlobRectangle from '../../../../../public/assets/recipes/categories/blob-rectangle.webp'
import CompareMobile from '../../../../../public/assets/recipes/categories/compare-m.webp'
import Kibble from '../../../../../public/assets/recipes/categories/kibble.webp'
import Canned from '../../../../../public/assets/recipes/categories/Canned.webp'
import Cooked from '../../../../../public/assets/recipes/categories/cooked.webp'
import Frozen from '../../../../../public/assets/recipes/categories/frozen.webp'
import Wfr from '../../../../../public/assets/recipes/categories/wfr-food.webp'
import Image from 'next/image'
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import Slider from 'react-slick'
import { sliderSettings } from 'fixtures/slider'
import { ColorTokens } from 'stitches.config'

type CompareOwnProps = ComponentProps<typeof CompareRoot>
export type CompareProps = CompareOwnProps & {
  background?: ColorTokens
}

export const Compare = (props: CompareProps) => {
  const { t } = useTranslation()

  const { background } = props

  const optionList: string[] = ['kibble', 'canned', 'cooked', 'freezer']
  const imageList = [Kibble, Canned, Cooked, Frozen]

  return (
    <CompareRoot css={{ background: `$${background}` }} id="recipes-3">
      <SectionTitle color="primary-500" align="center" weight="semiBold" size="h1" as="h2">
        {t('recipes:overview.compare.title')}
      </SectionTitle>
      <TableContainer>
        <CompareImageWrapper>
          <Image src={Comparison} alt={t('recipes:overview.compare.vs')} />
        </CompareImageWrapper>
        <BlobRectangleWrapper>
          <Image src={BlobRectangle} alt="" />
        </BlobRectangleWrapper>
        <Table>
          <tbody>
            <tr>
              <BrandColumn></BrandColumn>
              <ThemColumn>
                <Text size="bodyExtraLarge" color="accent-black" weight="semiBold">
                  {t('recipes:overview.compare.them')}
                </Text>
              </ThemColumn>
              <UsColumn>
                <Text size="bodyExtraLarge" color="accent-black" weight="semiBold">
                  {t('recipes:overview.compare.us')}
                </Text>
              </UsColumn>
            </tr>
            {optionList.map((option, index) => (
              <Row key={index}>
                <td>
                  <Text underline color="accent-black" weight="semiBold" size="bodyExtraLarge">
                    {t(`recipes:overview.compare.${option}.label`)}
                  </Text>
                </td>
                <td>
                  <Text display={{'@initial': 'none', '@bp2': 'block'}} color="accent-black" size="bodyLarge">
                    <Trans i18nKey={`recipes:overview.compare.${option}.them`} components={{ br: <br /> }} />
                  </Text>
                </td>
                <td>
                  <Text display={{'@initial': 'none', '@bp2': 'block'}} color="primary-500" weight="semiBold" size="bodyLarge">
                    <Trans i18nKey={`recipes:overview.compare.${option}.us`} components={{ br: <br /> }} />
                  </Text>
                </td>
              </Row>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      <Blob5Wrapper>
        <Image src={Blob5} alt="" />
      </Blob5Wrapper>
      <Blob6Wrapper>
        <Image src={Blob6} alt="" />
      </Blob6Wrapper>
      <MobileWrapper>
        <Slider {...sliderSettings}>
          {optionList.map((option, index) => (
            <CarouselItem key={index}>
              <FoodWrapper>
                <Text color="accent-black" align="center" weight="semiBold" size="bodyExtraLarge">
                  Them: {t(`recipes:overview.compare.${option}.label`)}
                </Text>
                <FoodImage>
                  <Image
                    priority={true}
                    src={imageList[index]}
                    alt={t(`recipes:overview.compare.${option}.label`)}
                  />
                </FoodImage>
                <Text display={{'@initial': 'block', '@bp2': 'none'}} color="accent-black" align="center" weight="regular" size="body">
                  {t(`recipes:overview.compare.${option}.them-m`)}
                </Text>
              </FoodWrapper>
              <CompareImageWrapper>
                <Image priority={true} src={CompareMobile} alt={t('recipes:overview.compare.vs')} />
              </CompareImageWrapper>
              <FoodWrapper type="wfr">
                <Text color="primary-500" align="center" weight="semiBold" size="bodyExtraLarge">
                  Us:
                </Text>
                <FoodImage>
                  <Image priority={true} src={Wfr} alt={t('recipes:overview.compare.wfr')} />
                </FoodImage>
                <Text display={{'@initial': 'block', '@bp2': 'none'}} color="primary-500" align="center" weight="semiBold" size="body">
                  {t(`recipes:overview.compare.${option}.us-m`)}
                </Text>
              </FoodWrapper>
            </CarouselItem>
          ))}
        </Slider>
      </MobileWrapper>
    </CompareRoot>
  )
}

Compare.displayName = 'Compare'
